import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, User, Layers, ShoppingBasket, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { globalMonths, useFilters } from "@/context/FilterContext";
import { usePerm } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductAnalysis } from "@/components/products/ProductAnalysis";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { BasketTable } from "@/components/BasketTable";
import { CustomerPicker } from "@/components/CustomerPicker";
import { api, eur, num, errMsg } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";


const Kpi = ({ label, value, id }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1.5 font-heading text-xl font-semibold text-slate-900" data-testid={`basket-kpi-${id}`}>{value}</div>
  </div>
);

const exportCsv = (d, threshold) => downloadCsv(`paniere-${d.scope.name}`,
  ["prodotto", "ean", "penetrazione_%", "clienti", "scontrini", "quantita", "spesa", "quota_spesa_%", "prezzo_medio", "paniere"],
  d.products.map((p) => [p.name, p.ean, p.penetration, p.clients, p.receipts, p.qty, p.spend, p.share, p.avg_price, p.penetration >= threshold ? "si" : "no"]));

function BasketView() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [type, setType] = useState(params.get("segment") ? "segment" : "customer");
  const [customer, setCustomer] = useState(params.get("customer") ? { id: params.get("customer"), name: "" } : null);
  const [segmentId, setSegmentId] = useState(params.get("segment") || "");
  const [segments, setSegments] = useState([]);
  const { filters } = useFilters();
  const gm = globalMonths(filters, [1, 3, 6, 12, 24]);
  const months = gm.months;
  const [threshold, setThreshold] = useState(type === "customer" ? 3 : 20);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/segments").then((r) => setSegments(r.data)).catch(() => {}); }, []);

  const target = type === "customer" ? customer?.id : segmentId;
  useEffect(() => {
    if (!target) { setParams({}, { replace: true }); setData(null); return; }
    let live = true;
    setLoading(true); setError(""); setData(null);
    const q = type === "customer" ? { customer_id: target } : { segment_id: target };
    setParams(type === "customer" ? { customer: target } : { segment: target }, { replace: true });
    api.get("/basket", { params: { ...q, months } })
      .then((r) => live && setData(r.data))
      .catch((e) => live && setError(errMsg(e)))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [type, target, months]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchType = (t) => { setType(t); setThreshold(t === "customer" ? 3 : 20); setData(null); setError(""); };
  const core = useMemo(() => (data?.products || []).filter((p) => p.penetration >= threshold), [data, threshold]);
  const coreSpend = core.reduce((a, p) => a + p.share, 0);

  return (
    <div>
      <PageHeader title="Paniere prodotti" subtitle="I prodotti che definiscono la spesa abituale di un cliente o di un segmento">
        {data?.scope.type === "customer" && <Button data-testid="basket-ai-profile-button" onClick={() => nav(`/analisi-ia?scope=customer&id=${data.scope.id}`)} className="bg-blue-600 hover:bg-blue-700"><Sparkles className="mr-2 h-4 w-4" />Profilo con IA</Button>}
        {data && <Button variant="outline" data-testid="basket-export-button" onClick={() => { exportCsv(data, threshold); toast.success("Paniere esportato"); }}><Download className="mr-2 h-4 w-4" />Esporta CSV</Button>}
      </PageHeader>

      <Panel className="mb-6" testId="basket-controls">
        <div className="grid gap-4 p-5 lg:grid-cols-[auto_1fr_200px]">
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
            {[["customer", "Singolo cliente", User], ["segment", "Segmento", Layers]].map(([k, l, Icon]) => (
              <button key={k} data-testid={`basket-type-${k}`} onClick={() => switchType(k)}
                className={`flex items-center justify-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${type === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <Icon className="h-4 w-4" />{l}
              </button>
            ))}
          </div>
          {type === "customer" ? <CustomerPicker value={customer?.name ? customer : data?.scope.type === "customer" ? data.scope : null} onPick={setCustomer} /> : (
            <Select value={segmentId} onValueChange={setSegmentId}>
              <SelectTrigger data-testid="basket-segment-select"><SelectValue placeholder="Scegli un segmento" /></SelectTrigger>
              <SelectContent className="bg-white">{segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({num(s.count)} clienti)</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div data-testid="basket-period-label" className="flex min-h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">Periodo: {gm.label}</div>
        </div>
      </Panel>

      {error && <div data-testid="basket-error" className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}
      {!target && !error && <Panel><Empty text={type === "customer" ? "Cerca e seleziona un cliente per calcolarne il paniere." : "Seleziona un segmento per calcolarne il paniere."} /></Panel>}
      {loading && <div className="grid gap-4 md:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}<p className="text-xs text-slate-400 md:col-span-3">Analisi delle righe scontrino in corso… per i segmenti grandi può richiedere qualche secondo.</p></div>}

      {data && !loading && (
        <div className="animate-rise space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <ShoppingBasket className="h-5 w-5 text-blue-600" />
            <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="basket-scope-name">{data.scope.name}</h2>
            {data.scope.tier && <TierBadge tier={data.scope.tier} />}
            {data.scope.type === "segment" && <span className="text-sm text-slate-500">{num(data.scope.members)} clienti nel segmento{data.scope.capped ? ` · analisi su un campione casuale di ${num(data.scope.sampled)} clienti` : ""}</span>}
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <Kpi id="clients" label="Clienti acquirenti" value={num(data.clients)} />
            <Kpi id="receipts" label="Scontrini" value={num(data.receipts)} />
            <Kpi id="spend" label="Spesa nel periodo" value={eur(data.spend)} />
            <Kpi id="avg-receipt" label="Scontrino medio" value={eur(data.avg_receipt)} />
            <Kpi id="items" label="Articoli per scontrino" value={data.items_per_receipt.toLocaleString("it-IT")} />
            <Kpi id="promo" label="Spesa in promozione" value={`${data.promo_share.toLocaleString("it-IT")}%`} />
          </div>

          <Panel title="Paniere base" testId="basket-core-panel" action={<span className="text-xs text-slate-500" data-testid="basket-core-summary">{core.length} prodotti · {coreSpend.toLocaleString("it-IT", { maximumFractionDigits: 1 })}% della spesa</span>}>
            <div className="space-y-4 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="text-sm text-slate-600 sm:w-72">
                  Prodotti presenti in almeno il <strong data-testid="basket-threshold-value">{threshold}%</strong> {data.scope.type === "customer" ? "degli scontrini" : "dei clienti"}
                </span>
                <Slider data-testid="basket-threshold-slider" className="flex-1" min={1} max={60} step={1} value={[threshold]} onValueChange={(v) => setThreshold(v[0])} />
              </div>
              {core.length === 0 ? <p className="text-sm text-slate-400">Nessun prodotto supera la soglia: abbassala per vedere il paniere.</p> : (
                <div className="flex flex-wrap gap-2" data-testid="basket-core-list">
                  {core.map((p) => (
                    <span key={p.ean} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-800">
                      {p.name}<span className="font-mono text-[10px] text-blue-500">{p.penetration.toLocaleString("it-IT")}%</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Panel>

          <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr] [&>*]:min-w-0">
            <BasketTable products={data.products} threshold={threshold} single={data.scope.type === "customer"} months={gm.rate} />
            <div className="space-y-6">
              <Panel title="Spesa per giorno della settimana" testId="basket-weekday-chart">
                <div className="h-56 p-4">
                  <ResponsiveContainer>
                    <BarChart data={data.weekdays}>
                      <CartesianGrid stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={12} />
                      <YAxis tickLine={false} axisLine={false} fontSize={11} width={70} tickFormatter={(v) => num(v)} />
                      <Tooltip formatter={(v) => eur(v)} />
                      <Bar dataKey="spend" name="Spesa" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <Panel title="Punti vendita principali" testId="basket-stores">
                {data.stores.length === 0 ? <Empty text="Nessun dato." /> : data.stores.map((s) => (
                  <div key={s.name} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-2.5 text-sm last:border-0">
                    <span className="truncate text-slate-700">{s.name}</span><span className="font-mono text-xs text-slate-600">{eur(s.spend)}</span>
                  </div>
                ))}
              </Panel>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Basket() {
  const [params, setParams] = useSearchParams();
  const products = usePerm("products") !== "none";
  const view = params.get("view") === "prodotti" && products ? "prodotti" : "paniere";
  return (
    <Tabs value={view} onValueChange={(v) => setParams(v === "prodotti" ? { view: v } : {}, { replace: true })}>
      <TabsList className="mb-6"><TabsTrigger value="paniere" data-testid="basket-tab-basket">Paniere cliente / segmento</TabsTrigger>
        {products && <TabsTrigger value="prodotti" data-testid="basket-tab-products">Analisi prodotti</TabsTrigger>}</TabsList>
      <TabsContent value="paniere">{view === "paniere" && <BasketView />}</TabsContent>
      <TabsContent value="prodotti">{view === "prodotti" && <>
        <PageHeader title="Analisi prodotti" subtitle="Chi compra uno o più prodotti, dove, quanto e quanto in promozione" />
        <ProductAnalysis /></>}</TabsContent>
    </Tabs>
  );
}
